import { useMemo, useState } from "react";
import { useForm } from "@tanstack/react-form";
import { Loader2, Mail } from "lucide-react";
import { toast } from "react-toastify";
import { Input } from "@components/ui/input";
import { Button } from "@components/ui/button";
import { Label } from "@components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@components/ui/select";
import { cn } from "@/lib/utils";
import { getReadableErrorMessage } from "@pages/users/form/formHelpers";
import { buildContractSchema } from "./buildContractSchema";
import type { ContractField } from "./buildContractSchema";

interface Props {
  fields: ContractField[];
  countries: string[];
  prefill: Record<string, unknown> | null;
  deliveryEmail: string;
  submitLabel: string;
  onSubmit: (values: Record<string, string>) => Promise<void>;
}

interface Section {
  key: string;
  title: string;
  fields: ContractField[];
}

/** Group the flat field list into the sections the server assigned. */
const groupIntoSections = (fields: ContractField[]): Section[] => {
  const sections: Section[] = [];
  for (const field of fields) {
    const last = sections[sections.length - 1];
    if (last && last.key === field.section) {
      last.fields.push(field);
      continue;
    }
    sections.push({
      key: field.section,
      title: field.sectionTitle,
      fields: [field],
    });
  }
  return sections;
};

export default function ContractForm({
  fields,
  countries,
  prefill,
  deliveryEmail,
  submitLabel,
  onSubmit,
}: Props) {
  const [submitError, setSubmitError] = useState<string | null>(null);

  const sections = useMemo(() => groupIntoSections(fields), [fields]);

  const schema = useMemo(
    () => buildContractSchema(fields, countries),
    [fields, countries],
  );

  const defaultValues = useMemo(() => {
    const values: Record<string, string> = {};
    for (const field of fields) {
      const prefillValue = prefill?.[field.key];
      values[field.key] =
        prefillValue !== undefined && prefillValue !== null
          ? String(prefillValue)
          : "";
    }
    return values;
  }, [fields, prefill]);

  const form = useForm({
    defaultValues,
    validators: {
      // buildContractSchema builds its shape from the server-driven field
      // list, so its static input type can't be narrowed to match the
      // form's Record<string, string> defaultValues ahead of time.
      onChange: schema as never,
    },
    onSubmit: async ({ value }) => {
      setSubmitError(null);
      try {
        await onSubmit(value);
      } catch (err: unknown) {
        const message = getReadableErrorMessage(
          err,
          "Die Angaben konnten nicht gespeichert werden.",
        );
        setSubmitError(message);
        toast.error(message);
      }
    },
  });

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        e.stopPropagation();
        form.handleSubmit();
      }}
      className="space-y-6"
    >
      {sections.map((section) => (
        <section key={section.key} className="space-y-4">
          <h3 className="text-sm font-semibold text-slate-900">
            {section.title}
          </h3>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            {section.fields.map((contractField) => (
              <form.Field key={contractField.key} name={contractField.key}>
                {(field) => {
                  const showError =
                    field.state.meta.isTouched || form.state.isSubmitted;
                  const error = showError
                    ? (field.state.meta.errors[0] as unknown as
                        | string
                        | { message?: string }
                        | undefined)
                    : undefined;
                  const errorMessage =
                    typeof error === "string" ? error : error?.message;

                  const cellClass =
                    contractField.width === "half"
                      ? "sm:col-span-1"
                      : "sm:col-span-2";

                  if (contractField.fieldType === "country") {
                    return (
                      <div className={cn("space-y-1.5", cellClass)}>
                        <Label htmlFor={field.name}>
                          {contractField.label}
                          {contractField.required && (
                            <span className="ml-0.5 text-red-600">*</span>
                          )}
                        </Label>
                        <Select
                          value={field.state.value || undefined}
                          onValueChange={(value) => {
                            field.handleChange(value);
                            field.handleBlur();
                          }}
                        >
                          <SelectTrigger
                            id={field.name}
                            aria-invalid={Boolean(errorMessage)}
                            className={cn(
                              errorMessage && "border-red-500",
                            )}
                          >
                            <SelectValue placeholder="Bitte auswählen" />
                          </SelectTrigger>
                          <SelectContent>
                            {countries.map((country) => (
                              <SelectItem key={country} value={country}>
                                {country}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        {contractField.helpText && !errorMessage && (
                          <p className="text-xs text-slate-500">
                            {contractField.helpText}
                          </p>
                        )}
                        {errorMessage && (
                          <p className="text-xs text-red-600" role="alert">
                            {errorMessage}
                          </p>
                        )}
                      </div>
                    );
                  }

                  return (
                    <div className={cellClass}>
                      <Input
                        label={contractField.label}
                        type={
                          contractField.fieldType === "phone" ? "tel" : "text"
                        }
                        required={contractField.required}
                        name={field.name}
                        id={field.name}
                        placeholder={contractField.placeholder}
                        value={field.state.value}
                        onChange={(e) => field.handleChange(e.target.value)}
                        onBlur={field.handleBlur}
                        error={errorMessage}
                      />
                      {contractField.helpText && !errorMessage && (
                        <p className="mt-1 text-xs text-slate-500">
                          {contractField.helpText}
                        </p>
                      )}
                    </div>
                  );
                }}
              </form.Field>
            ))}
          </div>
        </section>
      ))}

      {deliveryEmail && (
        <p className="flex items-center gap-2 rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-600">
          <Mail className="h-4 w-4 shrink-0 text-slate-400" />
          <span>
            Wir senden Ihnen den Vertrag per E-Mail an:{" "}
            <strong className="font-semibold text-slate-900">
              {deliveryEmail}
            </strong>
          </span>
        </p>
      )}

      {submitError ? <p className="text-sm text-red-600">{submitError}</p> : null}

      <form.Subscribe selector={(state) => [state.canSubmit, state.isSubmitting]}>
        {([canSubmit, isSubmitting]) => (
          <Button
            submit={true}
            style="primary"
            className="w-full"
            disabled={!canSubmit || isSubmitting}
          >
            <div className="flex items-center justify-center gap-1.5">
              <span>{submitLabel}</span>
              {isSubmitting && (
                <Loader2 className="h-4 w-4 animate-spin text-white" />
              )}
            </div>
          </Button>
        )}
      </form.Subscribe>
    </form>
  );
}
